(() => {
  const STORAGE_KEY = 'havenstock-personalization-v1';
  const MAX_VIEWS = 12;
  const MAX_SEARCHES = 8;
  const MAX_RESULTS = 8;
  const context = window.HavenstockPersonalizationContext || {};

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const readState = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return {
        views: Array.isArray(parsed.views) ? parsed.views : [],
        searches: Array.isArray(parsed.searches) ? parsed.searches : []
      };
    } catch (_) {
      return { views: [], searches: [] };
    }
  };

  const writeState = (state) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  };

  const clearState = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    document.querySelector('[data-hs-personalized-feed]')?.setAttribute('hidden', '');
  };

  const preferencesAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.());
    } catch (_) {
      return false;
    }
  };

  const withPrivacy = (callback) => {
    const run = () => {
      if (preferencesAllowed()) callback();
    };

    if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed) {
      run();
      return;
    }

    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => { if (!error) run(); }
      );
    }
  };

  const cleanTerm = (value) => String(value || '').trim().replace(/\s+/g, ' ').slice(0, 80);

  const recordSearch = (term) => {
    term = cleanTerm(term);
    if (term.length < 2) return;
    const state = readState();
    state.searches = [
      { term, ts: Date.now() },
      ...state.searches.filter((item) => item?.term?.toLowerCase() !== term.toLowerCase())
    ].slice(0, MAX_SEARCHES);
    writeState(state);
  };

  const recordView = (product) => {
    if (!product?.id || !product?.handle) return;
    const id = String(product.id);
    const state = readState();
    state.views = [
      {
        id,
        handle: String(product.handle),
        title: String(product.title || ''),
        vendor: String(product.vendor || ''),
        type: String(product.type || ''),
        ts: Date.now()
      },
      ...state.views.filter((item) => String(item?.id) !== id)
    ].slice(0, MAX_VIEWS);
    writeState(state);
  };

  const formatMoney = (cents) => {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    const currency = window.Shopify?.currency?.active || context.currency || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency
      }).format(n / 100);
    } catch (_) {
      return '£' + (n / 100).toFixed(2);
    }
  };

  const productKey = (product) => String(product?.handle || product?.id || product?.url || '');

  const normalizeImage = (product) => {
    const image = product?.featured_image;
    if (typeof image === 'string') return image;
    return image?.url || image?.src || product?.image || product?.images?.[0] || '';
  };

  const addScored = (map, product, score) => {
    if (!product || product.available === false || !product.url) return;
    const key = productKey(product);
    if (!key) return;
    const existing = map.get(key);
    if (!existing || score > existing.score) map.set(key, { product, score });
  };

  const fetchRelated = async (view, viewIndex, map) => {
    const id = encodeURIComponent(view.id);
    const url = rootUrl() + 'recommendations/products.json?product_id=' + id + '&limit=6&intent=related';
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const data = await response.json();
    (data.products || []).forEach((product, index) => {
      addScored(map, product, 120 - (viewIndex * 18) - index);
    });
  };

  const fetchSearchMatches = async (search, searchIndex, map) => {
    const q = encodeURIComponent(search.term);
    const url = rootUrl() + 'search/suggest.json?q=' + q +
      '&resources[type]=product&resources[limit]=6' +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=title,product_type,tag,vendor,variants.title';
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const data = await response.json();
    const products = data?.resources?.results?.products || [];
    products.forEach((product, index) => {
      addScored(map, product, 80 - (searchIndex * 14) - index);
    });
  };

  const card = (product) => {
    const link = document.createElement('a');
    link.className = 'hs-for-you__card';
    link.href = product.url;

    const media = document.createElement('div');
    media.className = 'hs-for-you__media';

    const imageUrl = normalizeImage(product);
    if (imageUrl) {
      const img = document.createElement('img');
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 'width=480';
      img.alt = product.featured_image?.alt || product.title || '';
      media.appendChild(img);
    }

    const price = Number(product.price ?? product.price_min);
    const compare = Number(product.compare_at_price ?? product.compare_at_price_min);
    if (Number.isFinite(compare) && Number.isFinite(price) && compare > price) {
      const badge = document.createElement('span');
      badge.className = 'hs-for-you__sale';
      badge.textContent = 'Sale';
      media.appendChild(badge);
    }

    const vendor = document.createElement('div');
    vendor.className = 'hs-for-you__vendor';
    vendor.textContent = product.vendor || '';

    const title = document.createElement('div');
    title.className = 'hs-for-you__title';
    title.textContent = product.title || '';

    const priceRow = document.createElement('div');
    priceRow.className = 'hs-for-you__price';
    if (Number.isFinite(price)) {
      const current = document.createElement('span');
      current.textContent = formatMoney(price);
      priceRow.appendChild(current);
    }
    if (Number.isFinite(compare) && Number.isFinite(price) && compare > price) {
      const old = document.createElement('s');
      old.textContent = formatMoney(compare);
      priceRow.appendChild(old);
    }

    link.append(media, vendor, title, priceRow);
    return link;
  };

  const renderFeed = async () => {
    const feed = document.querySelector('[data-hs-personalized-feed]');
    const grid = document.querySelector('[data-hs-personalized-grid]');
    if (!feed || !grid) return;

    const state = readState();
    if (!state.views.length && !state.searches.length) return;

    const scored = new Map();
    const tasks = [
      ...state.views.slice(0, 3).map((view, index) => fetchRelated(view, index, scored)),
      ...state.searches.slice(0, 2).map((search, index) => fetchSearchMatches(search, index, scored))
    ];

    await Promise.allSettled(tasks);

    const viewedHandles = new Set(state.views.map((item) => item.handle).filter(Boolean));
    const products = [...scored.values()]
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.product)
      .filter((product) => !viewedHandles.has(product.handle))
      .slice(0, MAX_RESULTS);

    if (products.length < 2) return;

    grid.replaceChildren(...products.map(card));
    feed.hidden = false;
  };

  const init = () => {
    if (context.pageType === 'product' && context.product) recordView(context.product);
    if (context.pageType === 'search' && context.searchTerm) recordSearch(context.searchTerm);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('.hs-app-search')) return;
      const term = form.querySelector('input[name="q"]')?.value;
      if (preferencesAllowed()) recordSearch(term);
    });

    renderFeed().catch(() => {});
  };

  const start = () => withPrivacy(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    if (event.detail?.preferencesAllowed) {
      init();
    } else {
      clearState();
    }
  });
})();